import { useForm } from '@tanstack/react-form';
import { Braces, FileText, Tag } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@sara/ui/components/button';
import { Input } from '@sara/ui/components/input';
import { Field, FieldContent, FieldError, FieldLabel } from '@sara/ui/components/field';
import { extractionFormSchema, type ExtractionFormValues } from './schemas';

interface ExtractionFormProps {
  mode: 'create' | 'newVersion';
  defaultValues?: Partial<ExtractionFormValues>;
  onSubmit: (values: ExtractionFormValues) => void;
  isPending: boolean;
  error?: string | null;
}

const DEFAULT_VALUES: ExtractionFormValues = {
  name: '',
  description: '',
  prompt: '',
  outputSchema: '',
};

const textareaClassName =
  'flex w-full rounded-md border border-border/70 bg-background px-3 py-2.5 font-mono text-[12.5px] leading-relaxed text-foreground/90 ring-offset-background placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

const subLabelClassName = 'text-[12px] font-medium text-foreground/80';

function SectionCard({
  icon: Icon,
  title,
  hint,
  children,
}: {
  icon: LucideIcon;
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-border/70 bg-card">
      <header className="flex items-start gap-3 border-b border-border/60 bg-muted/30 px-4 py-3">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border/70 bg-background text-foreground/70">
          <Icon className="h-4 w-4" strokeWidth={1.75} />
        </span>
        <div className="flex flex-col min-w-0 pt-0.5">
          <h3 className="text-[14px] font-semibold leading-tight text-foreground">{title}</h3>
          {hint && <p className="text-[12.5px] leading-snug text-muted-foreground">{hint}</p>}
        </div>
      </header>
      <div className="p-4 sm:p-5 space-y-5">{children}</div>
    </section>
  );
}

export function ExtractionForm({
  mode,
  defaultValues,
  onSubmit,
  isPending,
  error,
}: ExtractionFormProps) {
  const form = useForm({
    defaultValues: { ...DEFAULT_VALUES, ...defaultValues },
    validators: {
      onSubmit: extractionFormSchema,
    },
    onSubmit: ({ value }) => {
      onSubmit(value);
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void form.handleSubmit();
      }}
      autoComplete="off"
      className="space-y-5"
    >
      {error && (
        <div className="rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {mode === 'create' && (
        <SectionCard icon={Tag} title="Details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
            <form.Field name="name">
              {(field) => {
                const hasError = field.state.meta.errors.length > 0;
                return (
                  <Field data-invalid={hasError || undefined}>
                    <FieldLabel htmlFor={field.name} className={subLabelClassName}>
                      Name <span className="text-destructive">*</span>
                    </FieldLabel>
                    <FieldContent>
                      <Input
                        id={field.name}
                        placeholder="e.g. Candidate profile extraction"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        disabled={isPending}
                        aria-invalid={hasError}
                        autoComplete="off"
                        className="h-9"
                      />
                      <FieldError errors={field.state.meta.errors} />
                    </FieldContent>
                  </Field>
                );
              }}
            </form.Field>

            <form.Field name="description">
              {(field) => {
                const hasError = field.state.meta.errors.length > 0;
                return (
                  <Field data-invalid={hasError || undefined}>
                    <FieldLabel htmlFor={field.name} className={subLabelClassName}>
                      Description
                    </FieldLabel>
                    <FieldContent>
                      <Input
                        id={field.name}
                        placeholder="Optional description…"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        disabled={isPending}
                        aria-invalid={hasError}
                        autoComplete="off"
                        className="h-9"
                      />
                      <FieldError errors={field.state.meta.errors} />
                    </FieldContent>
                  </Field>
                );
              }}
            </form.Field>
          </div>
        </SectionCard>
      )}

      <SectionCard
        icon={FileText}
        title="Prompt"
        hint="Instructions for the AI on what to extract from the conversation."
      >
        <form.Field name="prompt">
          {(field) => {
            const hasError = field.state.meta.errors.length > 0;
            return (
              <Field data-invalid={hasError || undefined}>
                <FieldLabel htmlFor={field.name} className={subLabelClassName}>
                  Prompt <span className="text-destructive">*</span>
                </FieldLabel>
                <FieldContent>
                  <textarea
                    id={field.name}
                    placeholder="Extract the following fields from the conversation…"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    disabled={isPending}
                    aria-invalid={hasError}
                    rows={10}
                    className={textareaClassName}
                    autoComplete="off"
                  />
                  <FieldError errors={field.state.meta.errors} />
                </FieldContent>
              </Field>
            );
          }}
        </form.Field>
      </SectionCard>

      <SectionCard
        icon={Braces}
        title="Output schema"
        hint='A valid JSON Schema (type: "object") describing the extraction output shape.'
      >
        <form.Field name="outputSchema">
          {(field) => {
            const hasError = field.state.meta.errors.length > 0;
            return (
              <Field data-invalid={hasError || undefined}>
                <FieldLabel htmlFor={field.name} className={subLabelClassName}>
                  Output schema <span className="text-destructive">*</span>
                </FieldLabel>
                <FieldContent>
                  <textarea
                    id={field.name}
                    placeholder={`{\n  "type": "object",\n  "properties": {\n    "field": { "type": "string" }\n  },\n  "required": ["field"]\n}`}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    disabled={isPending}
                    aria-invalid={hasError}
                    rows={10}
                    className={textareaClassName}
                    autoComplete="off"
                  />
                  <FieldError errors={field.state.meta.errors} />
                </FieldContent>
              </Field>
            );
          }}
        </form.Field>
      </SectionCard>

      <div className="sticky bottom-4 mb-4 mt-2 rounded-lg border border-border/70 bg-background/95 backdrop-blur px-4 py-2.5 flex items-center justify-between gap-3">
        <span className="text-[12px] text-muted-foreground hidden sm:inline">
          {mode === 'newVersion'
            ? 'Versions are immutable — submitting creates a new active version.'
            : 'Fill in all required fields before submitting.'}
        </span>
        <div className="flex items-center gap-2 ml-auto">
          <Button
            type="submit"
            variant="tinted"
            shape="pill"
            size="sm"
            disabled={isPending}
            className="gap-1.5"
          >
            {isPending
              ? mode === 'create'
                ? 'Creating…'
                : 'Creating version…'
              : mode === 'create'
                ? 'Create extraction'
                : 'Create version'}
          </Button>
        </div>
      </div>
    </form>
  );
}
